import React from 'react';
import CosmeticRepairRequestPage from './CosmeticRepairRequestPage';
import {
    CosmeticPageErrors,
    CosmeticProcessStages,
    CosmeticRepairRequestType,
    Panels
} from './CosmeticRepairRequestTypes';
import CosmeticRepairAddressPage from './CosmeticRepairAddressPage';
import CosmeticRepairRequestSummary from './CosmeticRepairRequestSummary';
import { AddressType, VehicleType } from '../../common/CommonTypes';
import LoadingContainer from '../../common/loadingContainer/LoadingContainer';
import { t } from 'i18next';

export type CosmeticRepairRequestProps = {
    cosmeticRequests: CosmeticRepairRequestType[];
    cosmeticProcessStage: CosmeticProcessStages;
    cosmeticFormError?: CosmeticPageErrors;
    currentCosmeticRepairRequest: CosmeticRepairRequestType;
    repairAddressSelectedIndex: number;
    loadingOnSubmitRequests: boolean;
    permissionGranted: boolean;
    vehicle: VehicleType;
    repairAddresses: Array<AddressType>;
    scrollToAddress?: boolean;
    showRepairRequestForm: boolean;
    showCancelAlert: boolean;
    showDamageDepthQuestion: () => boolean;
    onSaveCosmetic: () => boolean;
    onChange: (key: string, value: string) => void;
    resetGeneralErrors: () => void;
    onChangeMileage: (event: React.FormEvent<HTMLInputElement>) => void;
    onChangeDamageType: (selectionName: string) => void;
    onChangeDamageDepth: (selectedValue: string) => void;
    onAddCosmeticRequest: () => void;
    onUploadImageChange: (
        imageType: string,
        uploadedPath: string,
        imageId: string,
        image: string,
        filename: string
    ) => void;
    onContinueToRepairAddress: () => void;
    onRepairAddressSelectionChanged: (index: number) => void;
    onSubmitCosmeticRepairRequests: () => void;
    onUpdateCosmeticRequest: (requestId: number) => void;
    onSelectedPanelChanged: (selected: boolean, panel: Panels) => void;
    onContinueToCosmeticSummary: () => void;
    onUpdateAddressClickFromSummary: (scrollToAddress) => void;
    onChangeStage: (CosmeticProcessStages) => void;
    onUpdateCosmeticClickFromSummary: (requestId: number) => void;
    onShowRepairRequestForm: (show: boolean) => void;
    onTogglePermission: (isChecked: boolean) => void;
    onCancelClick: () => void;
    onCancelRepairRequest: () => void;
    onCancelUndo: () => void;
    onDeleteCosmeticRequest: (requestId: number) => void;
};

const CosmeticRepairRequest = ({
    cosmeticRequests,
    scrollToAddress = false,
    showRepairRequestForm,
    currentCosmeticRepairRequest,
    cosmeticProcessStage,
    cosmeticFormError,
    repairAddressSelectedIndex,
    repairAddresses,
    loadingOnSubmitRequests,
    permissionGranted,
    vehicle,
    showCancelAlert,
    onChange,
    onSaveCosmetic,
    resetGeneralErrors,
    onChangeMileage,
    onChangeDamageType,
    onChangeDamageDepth,
    showDamageDepthQuestion,
    onAddCosmeticRequest,
    onUploadImageChange,
    onContinueToRepairAddress,
    onRepairAddressSelectionChanged,
    onSubmitCosmeticRepairRequests,
    onUpdateCosmeticRequest,
    onSelectedPanelChanged,
    onContinueToCosmeticSummary,
    onUpdateAddressClickFromSummary,
    onChangeStage,
    onUpdateCosmeticClickFromSummary,
    onShowRepairRequestForm,
    onTogglePermission,
    onCancelClick,
    onCancelRepairRequest,
    onCancelUndo,
    onDeleteCosmeticRequest
}: CosmeticRepairRequestProps): JSX.Element => {
    const cancelConfirmText = t('cancel_cosmetic_confirm_text');
    return (
        <>
            {cosmeticProcessStage === CosmeticProcessStages.General && (
                <>
                    <CosmeticRepairRequestPage
                        cosmeticRequests={cosmeticRequests}
                        showRepairRequestForm={showRepairRequestForm}
                        repairRequest={currentCosmeticRepairRequest}
                        onChange={onChange}
                        cosmeticFormError={cosmeticFormError}
                        onConfirm={onSaveCosmetic}
                        resetErrors={resetGeneralErrors}
                        onChangeMileage={onChangeMileage}
                        onChangeDamageType={onChangeDamageType}
                        onChangeDamageDepth={onChangeDamageDepth}
                        showDamageDepthQuestion={showDamageDepthQuestion}
                        onAddCosmeticRequest={onAddCosmeticRequest}
                        onUploadImageChange={onUploadImageChange}
                        onContinueToRepairAddress={onContinueToRepairAddress}
                        onUpdateCosmeticRequest={onUpdateCosmeticRequest}
                        onSelectedPanelChanged={onSelectedPanelChanged}
                        onShowRepairRequestForm={onShowRepairRequestForm}
                        onCancelClick={onCancelClick}
                        cancelConfirmText={cancelConfirmText}
                        showCancelAlert={showCancelAlert}
                        onCancelRepairRequest={onCancelRepairRequest}
                        onCancelUndo={onCancelUndo}
                        onDeleteCosmeticRequest={onDeleteCosmeticRequest}
                    />
                </>
            )}
            {cosmeticProcessStage === CosmeticProcessStages.RepairAddress && (
                <CosmeticRepairAddressPage
                    cosmeticRequests={cosmeticRequests}
                    repairAddressSelectedIndex={repairAddressSelectedIndex}
                    repairAddresses={repairAddresses}
                    onRepairAddressSelectionChanged={
                        onRepairAddressSelectionChanged
                    }
                    onContinueToCosmeticSummary={onContinueToCosmeticSummary}
                    onBack={onChangeStage}
                    errors={cosmeticFormError?.cosmeticAddress}
                    scrollToAddress={scrollToAddress}
                    onCancelClick={onCancelClick}
                    cancelConfirmText={cancelConfirmText}
                    showCancelAlert={showCancelAlert}
                    onCancelRepairRequest={onCancelRepairRequest}
                    onCancelUndo={onCancelUndo}
                />
            )}
            {cosmeticProcessStage === CosmeticProcessStages.Summary && (
                <LoadingContainer
                    loading={loadingOnSubmitRequests}
                    message={t('registering_requests')}>
                    <CosmeticRepairRequestSummary
                        cosmeticRepairRequests={cosmeticRequests}
                        cosmeticRequestAmount={cosmeticRequests.length}
                        selectedRepairAddress={
                            repairAddresses[repairAddressSelectedIndex]
                        }
                        onSubmitCosmeticRepairRequests={
                            onSubmitCosmeticRepairRequests
                        }
                        permissionGranted={permissionGranted}
                        errors={cosmeticFormError}
                        vehicle={vehicle}
                        onTogglePermission={onTogglePermission}
                        onBack={onChangeStage}
                        onUpdateAddressClick={onUpdateAddressClickFromSummary}
                        onUpdateCosmeticClick={onUpdateCosmeticClickFromSummary}
                        onCancelClick={onCancelClick}
                        cancelConfirmText={cancelConfirmText}
                        showCancelAlert={showCancelAlert}
                        onCancelRepairRequest={onCancelRepairRequest}
                        onCancelUndo={onCancelUndo}
                    />
                </LoadingContainer>
            )}
        </>
    );
};

export default CosmeticRepairRequest;
